"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/navigation"

const slides = [
  {
    id: 1,
    title: "Your Health, Our Priority",
    subtitle: "Get genuine medicines delivered to your doorstep",
    description: "Shop from over 10,000+ authentic products with fast delivery across Kenya",
    image: "/banners/1.jpeg",
    cta: "Shop Now",
    // bgColor: "bg-gradient-to-r from-green-600 to-green-700",
    bgColor: "bg-[#0c7e40]",
    link: "/shop",
  },
  {
    id: 2,
    title: "Expert Consultation Available",
    subtitle: "Talk to licensed pharmacists",
    description: "Get professional advice on medications and health concerns from certified experts",
    image: "/banners/2.jpeg",
    cta: "Consult Now",
    // bgColor: "bg-gradient-to-r from-red-600 to-red-700",
    bgColor: "bg-[#5fb946]",
    link: "/contact",
  },  
  {
    id: 3,
    title: "Save Up to 30% Today",
    subtitle: "Special offers on health products",
    description: "Exclusive discounts on vitamins, supplements, and personal care items",
    image: "/banners/3.jpg",
    cta: "View Offers",
    // bgColor: "bg-gradient-to-r from-green-400 to-green-500",
    bgColor: "bg-[#ec1c25]",
    link: "/shop",
  },
]

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const router = useRouter()
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  return (
    <section className="relative h-[500px] overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
            index === currentSlide ? "translate-x-0" : index < currentSlide ? "-translate-x-full" : "translate-x-full"
          }`}
        >
          <div className={`${slide.bgColor} h-full flex items-center`}>
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-white space-y-6">
                  <h1 className="text-4xl lg:text-5xl font-bold leading-tight">{slide.title}</h1>
                  <h2 className="text-xl lg:text-2xl font-medium opacity-90">{slide.subtitle}</h2>
                  <p className="text-lg opacity-80 max-w-lg">{slide.description}</p>
                  <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100" onClick={() => router.push(slide.link)}>
                    {slide.cta}
                  </Button>
                </div>
                <div className="hidden lg:block">
                  <Image
                    src={slide.image || "/banners/no-img.jpg"}
                    alt={slide.title}
                    width={600}
                    height={400}
                    className="w-full h-96 object-cover rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/banners/no-img.jpg";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <Button
        variant="outline"
        size="icon"
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 border-white/30 text-white hover:bg-white/30"
        onClick={prevSlide}
      >
        <ChevronLeft className="w-5 h-5" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 border-white/30 text-white hover:bg-white/30"
        onClick={nextSlide}
      >
        <ChevronRight className="w-5 h-5" />
      </Button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full transition-colors ${index === currentSlide ? "bg-white" : "bg-white/50"}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>
    </section>
  )
}
